import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LOCALE_META, translate, type Locale, type MessageKey } from './i18n';

export type Theme = 'dark' | 'light';
export type TextSize = 'sm' | 'md' | 'lg';

interface Stored {
  locale: Locale;
  theme: Theme;
  size: TextSize;
  reduceMotion: boolean;
}

interface SettingsValue extends Stored {
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  setSize: (size: TextSize) => void;
  setReduceMotion: (value: boolean) => void;
  t: (key: MessageKey) => string;
}

const STORAGE_KEY = 'iii:settings';

const DEFAULTS: Stored = { locale: 'en', theme: 'dark', size: 'md', reduceMotion: false };

function readStored(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return {
      locale: parsed.locale && parsed.locale in LOCALE_META ? parsed.locale : DEFAULTS.locale,
      theme: parsed.theme === 'light' ? 'light' : 'dark',
      size: parsed.size === 'sm' || parsed.size === 'lg' ? parsed.size : 'md',
      reduceMotion: Boolean(parsed.reduceMotion),
    };
  } catch {
    return DEFAULTS;
  }
}

const SettingsContext = createContext<SettingsValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Stored>(readStored);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = state.locale;
    root.dir = LOCALE_META[state.locale].dir;
    root.dataset.theme = state.theme;
    root.dataset.size = state.size;
    root.classList.toggle('motion-off', state.reduceMotion);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage disabled: preferences last for the session only.
    }
  }, [state]);

  const update = useCallback((patch: Partial<Stored>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  const value = useMemo<SettingsValue>(
    () => ({
      ...state,
      setLocale: (locale) => update({ locale }),
      setTheme: (theme) => update({ theme }),
      setSize: (size) => update({ size }),
      setReduceMotion: (reduceMotion) => update({ reduceMotion }),
      t: (key) => translate(state.locale, key),
    }),
    [state, update],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsValue {
  const value = useContext(SettingsContext);
  if (!value) throw new Error('useSettings must be used inside <SettingsProvider>');
  return value;
}
